import type { GenesisConfig } from '../../src/core/types.js';
import type { FeeParams } from '../../src/core/curve.js';
import type { TokenSummary } from './api.js';

/**
 * The launch rules as the browser needs them for quotes and notices. Same
 * formulas as the ledger (src/core/ledger.ts › feesAt, walletCap); the ledger
 * is what decides, this only predicts.
 */
export function baseFees(g: GenesisConfig): FeeParams {
  return { tradeBps: BigInt(g.tradeBps), creatorBps: BigInt(g.creatorBps) };
}

export function feesAt(g: GenesisConfig, t: TokenSummary, now: number): FeeParams {
  const fees = baseFees(g);
  const base = fees.tradeBps + fees.creatorBps;
  const top = BigInt(g.snipeFeeBps ?? 0);
  const window = BigInt(g.snipeWindowMs ?? 0);
  const left = BigInt(t.launchEnd + (g.snipeWindowMs ?? 0) - Math.round(now));
  if (window === 0n || top <= base || now < t.launchEnd || left <= 0n) return fees;
  return { ...fees, poolBps: ((top - base) * left) / window };
}

/** Total fee in percent at `now` (1 normally, up to 30 right after the launch minute). */
export function feePct(g: GenesisConfig, t: TokenSummary, now: number): number {
  const f = feesAt(g, t, now);
  return Number(f.tradeBps + f.creatorBps + (f.poolBps ?? 0n)) / 100;
}

/** Most tokens one wallet may hold while the cap applies, or null. */
export function walletCap(g: GenesisConfig): bigint | null {
  return g.walletCapBps > 0 ? (BigInt(g.totalSupply) * BigInt(g.walletCapBps)) / 10_000n : null;
}

export function capActive(g: GenesisConfig, t: TokenSummary, now: number): boolean {
  return walletCap(g) !== null && now < t.capUntil;
}

export function mmss(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ------------------------------------------------------------ trader settings

const KEY = 'dazzle.trade';

export interface TradePrefs {
  /** Buy amounts in ZEC shown as one-tap buttons. */
  presets: string[];
  slippage: number;
  /** Tapping a preset trades at once instead of filling the form. */
  instant: boolean;
}

const DEFAULTS: TradePrefs = { presets: ['0.01', '0.05', '0.1', '0.5'], slippage: 5, instant: false };

export function loadPrefs(): TradePrefs {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as Partial<TradePrefs> | null;
    if (!raw) return DEFAULTS;
    const presets = Array.isArray(raw.presets) ? raw.presets.filter((p) => /^\d*\.?\d{1,8}$/.test(String(p))).slice(0, 6).map(String) : DEFAULTS.presets;
    return {
      presets: presets.length ? presets : DEFAULTS.presets,
      slippage: typeof raw.slippage === 'number' && raw.slippage > 0 && raw.slippage <= 50 ? raw.slippage : DEFAULTS.slippage,
      instant: raw.instant === true,
    };
  } catch {
    return DEFAULTS;
  }
}

export function savePrefs(p: TradePrefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* private window: settings last for this visit only */
  }
}
